import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useMutation } from '@tanstack/react-query';
import { useQueryCachePermissionMemberHelpers } from '../../permission/queries/useQueryCachePermissionMembersHelpers.js';
import { createOptimisticSpaceRoot } from '../utils/createOptimisticSpace.js';
import { useQueryCacheSpaceHelpers } from '../utils/useQueryCacheSpaceHelpers.js';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { PermissionRole } from '@fabric/woody-client';

const defaultMutationArg = {
  title: "New space",
  action: "spaces-list"
};
const useMutationCreateNewSpace = () => {
  const { client } = useWoody();
  const { data: user } = useQueryUserInfo();
  const { track } = useAnalytics();
  const { addNewSpaceToCache } = useQueryCacheSpaceHelpers();
  const { addSelfToCachedSpace } = useQueryCachePermissionMemberHelpers();
  return useMutation({
    mutationFn: async (vars) => {
      if (!user) throw new Error("User not authenticated");
      const { title } = {
        ...defaultMutationArg,
        ...vars
      };
      const res = await client.v2("/v2/spaces", {
        method: "post",
        body: {
          name: title
        }
      });
      return res;
    },
    onMutate: (vars) => {
      if (!user) return;
      const { title } = {
        ...defaultMutationArg,
        ...vars
      };
      const optimisticSpace = createOptimisticSpaceRoot(title);
      return {
        optimisticSpace,
        addSelfToCachedSpaceContext: addSelfToCachedSpace(optimisticSpace.id, PermissionRole.OWNER)
      };
    },
    onError: (_err, _vars, ctx) => {
      toast({
        content: "Could not create a space"
      });
      console.error(_err);
      ctx?.addSelfToCachedSpaceContext?.resetCacheToPreOptimisticState();
      ctx?.addSelfToCachedSpaceContext?.invalidateQueries();
    },
    onSuccess: (space, variables) => {
      track(AnalyticsEvents.CreatedSpace, {
        action: variables?.action ?? "spaces-list"
      });
      const nowIso = (/* @__PURE__ */ new Date()).toISOString();
      const addToQueriesCache = addNewSpaceToCache({
        createdAt: nowIso,
        modifiedAt: nowIso,
        folder: {
          id: space.id,
          name: space.name,
          childrenCount: 0,
          isPublic: false,
          isReadonly: false,
          memberCount: 1,
          icon: null,
          permissions: {
            role: PermissionRole.OWNER
          }
        },
        ...space.root,
        type: "SPACE",
        subtype: null,
        subFoldersCount: 0
      });
      addToQueriesCache.invalidateQueries();
    }
  });
};

export { useMutationCreateNewSpace };
//# sourceMappingURL=useMutationCreateNewSpace.js.map
